import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, Gift } from "lucide-react";
import { fetchProducts, fetchReviews, fetchFaqs, resolveMediaUrl } from "../lib/api";
import { useContent } from "../lib/useContent";
import Navbar from "../components/Navbar";
import Countdown from "../components/Countdown";
import ProductCard from "../components/ProductCard";
import HorizontalProductRow from "../components/HorizontalProductRow";
import CoulissesSection from "../components/CoulissesSection";
import TrustVideoHero from "../components/TrustVideoHero";
import TrustBadges from "../components/TrustBadges";
import Reviews from "../components/Reviews";
import FAQ from "../components/FAQ";
import Newsletter from "../components/Newsletter";
import BrandsCarousel from "../components/BrandsCarousel";
import Footer from "../components/Footer";

const MAISON_IMG = "https://images.pexels.com/photos/9790391/pexels-photo-9790391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HERO_FEMME_FALLBACK = "https://images.unsplash.com/photo-1615634260167-c8cdede054de?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400";
const HERO_HOMME_FALLBACK = "https://images.unsplash.com/photo-1523293182086-7651a899d37f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400";

// Niche brand roster (independent / artisanal maisons)
const NICHE_BRANDS = new Set([
  "Maison Alhambra",
  "Paris Corner",
  "Loui Martin",
  "French Avenue",
  "Maison Asrar",
  "Lattafa Pride",
]);

const Home = () => {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [target] = useState(() => Date.now() + 1000 * 60 * 60 * 47 + 1000 * 60 * 23); // ~47h
  const content = useContent();

  useEffect(() => {
    Promise.all([fetchProducts({ sort: "newest" }), fetchReviews(), fetchFaqs()])
      .then(([p, r, f]) => { setProducts(p); setReviews(r); setFaqs(f); })
      .catch(console.error);
  }, []);

  // --- Data slices -----------------------------------------------------
  const perfumes = products.filter((p) => (p.product_type || "perfume") === "perfume");
  const femmeProducts = perfumes.filter((p) => p.category === "Femme");
  const hommeProducts = perfumes.filter((p) => p.category === "Homme");
  const virauxTikTok = perfumes.filter((p) => p.badge === "Meilleure vente").slice(0, 10);
  const produitsRecents = perfumes.slice(0, 10);
  const meilleuresVentes = perfumes.filter((p) => p.badge === "Meilleure vente").slice(0, 6);
  const folieArabe = perfumes.filter((p) => p.collection === "Orient").slice(0, 6);
  const parfumsNiche = perfumes.filter((p) => NICHE_BRANDS.has(p.brand)).slice(0, 10);
  const watches = products.filter((p) => p.product_type === "watch");

  // Hero visuals — prefer a real product image when available
  const femmeImg = resolveMediaUrl(femmeProducts[0]?.image_main) || HERO_FEMME_FALLBACK;
  const hommeImg = resolveMediaUrl(hommeProducts[0]?.image_main) || HERO_HOMME_FALLBACK;

  // CMS overrides for hero eyebrow / subtitle
  const heroEyebrow = content.get("home.hero.eyebrow", "Nouvelle collection · Hiver 2026");

  return (
    <div data-testid="home-page">
      <Navbar />

      {/* HERO SPLIT — FEMME / HOMME ============================== */}
      <section data-testid="hero-split-section" className="relative bg-black">
        <div className="max-w-[1800px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-1 lg:gap-2 pt-24 md:pt-28">
          {/* FEMME */}
          <Link
            to="/recherche?category=Femme"
            data-testid="hero-femme-block"
            className="group relative aspect-[4/5] md:aspect-[4/5] lg:aspect-[3/4] overflow-hidden"
          >
            <img
              src={femmeImg}
              alt="Parfums Femme"
              className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-14 lg:p-16">
              <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.35em] mb-4">
                {heroEyebrow}
              </p>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.9] mb-6">
                Elle<span className="text-[color:var(--gold-soft)] italic">.</span>
              </h2>
              <p className="text-white/70 font-light max-w-md mb-8 text-sm md:text-base">
                Sillages floraux, gourmands, ambrés. Une signature qui se pose comme un secret sur la peau.
              </p>
              <span className="inline-flex items-center gap-3 label-caps text-white/80 group-hover:text-[color:var(--gold)] transition-colors text-[11px] tracking-[0.3em]">
                Parfums Femme <ArrowRight size={14} />
              </span>
            </div>
          </Link>

          {/* HOMME */}
          <Link
            to="/recherche?category=Homme"
            data-testid="hero-homme-block"
            className="group relative aspect-[4/5] md:aspect-[4/5] lg:aspect-[3/4] overflow-hidden"
          >
            <img
              src={hommeImg}
              alt="Parfums Homme"
              className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-14 lg:p-16">
              <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.35em] mb-4">
                Fragrances masculines
              </p>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.9] mb-6">
                Lui<span className="text-[color:var(--gold-soft)] italic">.</span>
              </h2>
              <p className="text-white/70 font-light max-w-md mb-8 text-sm md:text-base">
                Bois précieux, cuirs fumés, épices nobles. Un caractère affirmé, une élégance qui dure.
              </p>
              <span className="inline-flex items-center gap-3 label-caps text-white/80 group-hover:text-[color:var(--gold)] transition-colors text-[11px] tracking-[0.3em]">
                Parfums Homme <ArrowRight size={14} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* MARQUEE ============================== */}
      <section className="border-y border-white/5 py-6 overflow-hidden bg-[#080808]">
        <div className="marquee-track flex gap-16 whitespace-nowrap label-caps text-white/40">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 shrink-0">
              <span>Sélection premium</span>
              <span className="text-[color:var(--gold)]">✦</span>
              <span>Livraison offerte dès 60€ · 160€ à l'international</span>
              <span className="text-[color:var(--gold)]">✦</span>
              <span>Écrin de velours offert</span>
              <span className="text-[color:var(--gold)]">✦</span>
              <span>Paiement 100% sécurisé</span>
              <span className="text-[color:var(--gold)]">✦</span>
              <span>Retours sous 14 jours</span>
              <span className="text-[color:var(--gold)]">✦</span>
            </div>
          ))}
        </div>
      </section>

      {/* VIRAUX TIKTOK ============================== */}
      <HorizontalProductRow
        testId="viraux-tiktok-section"
        eyebrow="Vus & aimés sur TikTok"
        title="Les"
        emphasis="viraux"
        description="Les fragrances qui affolent les feeds — sélectionnées par notre communauté."
        products={virauxTikTok}
        seeAllHref="/recherche?sort=popular"
        seeAllLabel="Découvrir tous les viraux"
        showTikTokBadge
      />

      {/* PROMO BANNER 1 — Countdown -15% ============================== */}
      <section
        data-testid="promo-banner-countdown"
        className="py-20 md:py-28 relative overflow-hidden bg-gradient-to-r from-[#0a0703] via-[#0c0805] to-[#0a0703] border-y border-[color:var(--gold)]/10"
      >
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 30%, gold 1px, transparent 1px), radial-gradient(circle at 80% 70%, gold 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center relative">
          <p className="label-caps text-[color:var(--gold)] mb-6 flex items-center justify-center gap-2 text-[10px] tracking-[0.35em]">
            <Sparkles size={14} /> Offre exclusive · Édition limitée
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight">
            −15% sur toute la <em className="italic text-[color:var(--gold-soft)]">Maison</em>
          </h2>
          <p className="text-white/50 max-w-xl mx-auto mb-12 font-light">
            Avec le code <span className="text-[color:var(--gold)] label-caps text-xs">BIENVENUE</span> pour votre première commande. Écrin en velours offert.
          </p>
          <Countdown target={target} />
          <div className="mt-12">
            <Link to="/recherche" data-testid="countdown-cta" className="btn-gold">
              En profiter maintenant <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUITS RÉCENTS ============================== */}
      <HorizontalProductRow
        testId="recents-section"
        eyebrow="Dernières arrivées"
        title="Produits"
        emphasis="récents"
        description="Les toutes dernières signatures ajoutées à notre écrin."
        products={produitsRecents}
        seeAllHref="/recherche?sort=newest"
        seeAllLabel="Tout voir"
      />

      {/* PROMO BANNER 2 — Reassurance triple ============================== */}
      <section
        data-testid="reassurance-strip"
        className="py-16 md:py-20 bg-[#080808] border-y border-white/5"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
          <div className="flex items-start gap-5">
            <Truck size={30} strokeWidth={1} className="text-[color:var(--gold)] shrink-0 mt-1" />
            <div>
              <p className="label-caps text-white/45 text-[10px] mb-2 tracking-[0.3em]">Livraison offerte</p>
              <h3 className="font-serif text-2xl text-white leading-tight">Dès 60€ en France<br />160€ à l'international</h3>
            </div>
          </div>
          <div className="flex items-start gap-5">
            <Gift size={30} strokeWidth={1} className="text-[color:var(--gold)] shrink-0 mt-1" />
            <div>
              <p className="label-caps text-white/45 text-[10px] mb-2 tracking-[0.3em]">Écrin offert</p>
              <h3 className="font-serif text-2xl text-white leading-tight">Emballage velours<br />pour chaque commande</h3>
            </div>
          </div>
          <div className="flex items-start gap-5">
            <Sparkles size={30} strokeWidth={1} className="text-[color:var(--gold)] shrink-0 mt-1" />
            <div>
              <p className="label-caps text-white/45 text-[10px] mb-2 tracking-[0.3em]">Sélection experte</p>
              <h3 className="font-serif text-2xl text-white leading-tight">Fragrances éprouvées,<br />notes rigoureusement choisies</h3>
            </div>
          </div>
        </div>
      </section>

      {/* MEILLEURES VENTES ============================== */}
      {meilleuresVentes.length > 0 && (
        <section id="bestsellers" data-testid="bestsellers-section" className="py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
              <div>
                <p className="label-caps text-[color:var(--gold)] mb-6">Plébiscités par vous</p>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
                  Meilleures <em className="italic text-[color:var(--gold-soft)]">ventes</em>
                </h2>
              </div>
              <p className="text-white/50 font-light max-w-md">
                Les incontournables de la Maison — élus par notre communauté commande après commande.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {meilleuresVentes.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOLIE ARABE ============================== */}
      {folieArabe.length > 0 && (
        <section id="folie-arabe" data-testid="folie-arabe-section" className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
              <div>
                <p className="label-caps text-[color:var(--gold)] mb-6">Collection · Moyen-Orient</p>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
                  Folie <em className="italic text-[color:var(--gold-soft)]">arabe</em>
                </h2>
              </div>
              <p className="text-white/50 font-light max-w-md">
                L'héritage arabo-persan sublimé. Lattafa, Paris Corner, Maison Alhambra — des compositions ambrées, gourmandes, boisées, empreintes d'un raffinement millénaire.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {folieArabe.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
            <div className="text-center mt-14">
              <Link to="/collection/orient" data-testid="folie-arabe-see-all" className="btn-outline">
                Découvrir toute la collection <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* PARFUMS NICHE ============================== */}
      <HorizontalProductRow
        testId="niche-section"
        eyebrow="Maisons indépendantes"
        title="Parfums"
        emphasis="niche"
        description="Des créations rares, souvent confidentielles, portées par des artisans passionnés."
        products={parfumsNiche}
        seeAllHref="/recherche"
        seeAllLabel="Explorer les niches"
      />

      {/* BRAND CAROUSEL ============================== */}
      <BrandsCarousel />

      {/* WATCHES ============================== */}
      {watches.length > 0 && (
        <section id="montres" data-testid="watches-section" className="py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
              <div>
                <p className="label-caps text-[color:var(--gold)] mb-6">L'Art de Vivre · Montres</p>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
                  L'heure de <em className="italic text-[color:var(--gold-soft)]">l'élégance</em>
                </h2>
              </div>
              <p className="text-white/50 font-light max-w-md">
                Une sélection d'horlogerie qui prolonge l'exigence de la Maison. Des pièces au style affirmé, pensées pour marquer chaque instant avec caractère.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {watches.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* UPCOMING — LUNETTES & SACS ============================== */}
      <section id="upcoming" data-testid="upcoming-section" className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="label-caps text-[color:var(--gold)] mb-4">L'Art de Vivre · Prochaines pièces</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">
              Bientôt, un univers <em className="italic text-[color:var(--gold-soft)]">élargi</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link to="/lunettes" data-testid="upcoming-lunettes" className="group relative aspect-[4/3] overflow-hidden border border-white/10 hover:border-[color:var(--gold)] transition-colors">
              <div className="absolute inset-0 bg-gradient-to-br from-[#111] to-[#000]" />
              <div className="relative h-full flex flex-col items-center justify-center text-center px-8">
                <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4">Collection à venir</p>
                <h3 className="font-serif text-5xl md:text-6xl text-white group-hover:text-[color:var(--gold)] transition-colors">Lunettes</h3>
                <p className="text-white/50 text-sm mt-6 max-w-sm">Le regard sublimé. Une sélection d'yeux d'exception, entre acétate italien et titane léger.</p>
                <span className="mt-8 label-caps text-[10px] text-white/40 tracking-[0.3em] group-hover:text-[color:var(--gold)] transition-colors">En savoir plus <ArrowRight size={12} className="inline ml-1" /></span>
              </div>
            </Link>
            <Link to="/sacs" data-testid="upcoming-sacs" className="group relative aspect-[4/3] overflow-hidden border border-white/10 hover:border-[color:var(--gold)] transition-colors">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0f0a06] to-[#000]" />
              <div className="relative h-full flex flex-col items-center justify-center text-center px-8">
                <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4">Collection à venir</p>
                <h3 className="font-serif text-5xl md:text-6xl text-white group-hover:text-[color:var(--gold)] transition-colors">Sacs</h3>
                <p className="text-white/50 text-sm mt-6 max-w-sm">Cuirs pleine fleur, façonnés main. Des pièces sobres, taillées pour durer une vie.</p>
                <span className="mt-8 label-caps text-[10px] text-white/40 tracking-[0.3em] group-hover:text-[color:var(--gold)] transition-colors">En savoir plus <ArrowRight size={12} className="inline ml-1" /></span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* MAISON ============================== */}
      <section id="maison" data-testid="maison-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={MAISON_IMG} alt="La MAISON DOUBA" className="w-full h-full object-cover" />
          </div>
          <div className="lg:pl-8">
            <p className="label-caps text-[color:var(--gold)] mb-6">La Maison</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-8 leading-tight">
              L'art de la <em className="italic text-[color:var(--gold-soft)]">sélection</em>.<br />La signature d'une <em className="italic text-[color:var(--gold-soft)]">exigence</em>.
            </h2>
            <p className="text-white/60 font-light leading-relaxed mb-6">
              MAISON DOUBA est une maison de sélection dédiée aux amateurs de fragrances d'exception. Nous parcourons les créations les plus abouties pour vous offrir une collection resserrée, où chaque flacon a été choisi pour la noblesse de ses matières et la profondeur de sa composition.
            </p>
            <p className="text-white/60 font-light leading-relaxed mb-10">
              Notre promesse : réunir sous un même écrin des parfums d'une qualité irréprochable, accompagnés d'un service à la hauteur de leur prestige.
            </p>
            <div className="grid grid-cols-3 gap-6 border-t border-white/10 pt-10">
              <div>
                <p className="font-serif text-2xl md:text-3xl text-[color:var(--gold)] leading-tight">Qualité<br />premium</p>
                <p className="label-caps text-white/45 text-[10px] mt-3">Sélection experte</p>
              </div>
              <div>
                <p className="font-serif text-2xl md:text-3xl text-[color:var(--gold)] leading-tight">Livraison<br />rapide</p>
                <p className="label-caps text-white/45 text-[10px] mt-3">48 à 72h · Offerte dès 60€ (160€ international)</p>
              </div>
              <div>
                <p className="font-serif text-2xl md:text-3xl text-[color:var(--gold)] leading-tight">Service<br />dédié</p>
                <p className="label-caps text-white/45 text-[10px] mt-3">Conciergerie 7j/7</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrustVideoHero />
      <TrustBadges />
      <CoulissesSection />
      <Reviews reviews={reviews} />
      <FAQ faqs={faqs} />
      <Newsletter />
      <Footer />
    </div>
  );
};

export default Home;
